import { useState, useCallback } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'default' | 'destructive'
}

interface ConfirmDialogState extends ConfirmOptions {
  isOpen: boolean
  onConfirm: () => void
  onCancel?: () => void
}

export function useConfirm() {
  const [state, setState] = useState<ConfirmDialogState>({
    isOpen: false,
    message: '',
    onConfirm: () => {},
  })

  const confirm = useCallback((options: ConfirmOptions & {
    onConfirm: () => void
    onCancel?: () => void
  }) => {
    setState({
      isOpen: true,
      ...options,
    })
  }, [])

  const handleConfirm = useCallback(() => {
    state.onConfirm()
    setState(prev => ({ ...prev, isOpen: false }))
  }, [state])

  const handleCancel = useCallback(() => {
    state.onCancel?.()
    setState(prev => ({ ...prev, isOpen: false }))
  }, [state])

  const ConfirmDialog = useCallback(() => (
    <AlertDialog open={state.isOpen} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{state.title || 'Confirm Action'}</AlertDialogTitle>
          <AlertDialogDescription>
            {state.message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button 
              variant="outline"
              onClick={handleCancel}
            >
              {state.cancelLabel || 'Cancel'}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button 
              variant={state.variant === 'destructive' ? 'destructive' : 'default'}
              onClick={handleConfirm}
            >
              {state.confirmLabel || 'Confirm'}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ), [state, handleConfirm, handleCancel])

  return { confirm, ConfirmDialog }
}